import { motion } from "framer-motion";
import Layout from "@/components/Layout";
// SEO: Import SEO component for process explanation page.
import SEO from '@/components/SEO';
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export default function HowItWorks() {
  const steps = [
    {
      number: 1,
      title: "Choose an Asset",
      description:
        "Pick the symbol you want analyzed.",
    },
    {
      number: 2,
      title: "AI Starts the Analysis",
      description:
        "Our AI collects and evaluates real market data, identifies trends, and provides an estimated time to next analysis attempt.",
    },
    {
      number: 3,
      title: "Validated Analysis Output",
      description:
        "AI continuously compares previous forecasts with actual market movement. When model validation metrics align, you receive analysis insights and market data points. If not, the system withholds low-quality outputs and schedules a new analysis window.",
    },
    {
      number: 4,
      title: "Fair Token System",
      description:
        "Each analysis uses 1 token. If the market context doesn’t warrant a validated output, your reserved token may be returned per policy. You only pay for validated analysis outputs.",
    },
  ];

  const howSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'How It Works',
    'url': 'https://tradesagepro.com/how-it-works',
    'description': 'Overview of TradeSagePro AI market analysis process: asset selection, analysis, validation and fair token handling.',
    'about': { '@type': 'Organization', 'name': 'TradeSagePro' }
  };
  return (
    <Layout>
      {/* SEO TAGS: Educational page describing process; WebPage schema enhances topical relevance. */}
      <SEO
        title="How TradeSagePro Works | AI Market Analysis Process"
        description="See how TradeSagePro analyzes live market data with AI to deliver analysis insights — including suggested data points for initiation, risk management parameters, and target closure parameters — with fair, token-balanced usage."
        canonical="https://tradesagepro.com/how-it-works"
        type="article"
        jsonLd={howSchema}
      />
      {/* END SEO TAGS */}
      <section className="relative overflow-hidden bg-[hsl(226,26%,7%)]">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[hsl(193,100%,50%,0.08)] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-[hsl(260,100%,70%,0.06)] blur-3xl" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
              How TradeSagePro Delivers Validated Market Analysis
            </h1>
            <p className="mt-4 text-[hsl(210,9%,80%)] text-base sm:text-lg max-w-2xl mx-auto">
              Smart. Transparent. Reliable.
            </p>
            <p className="mt-3 text-[hsl(210,9%,75%)] text-sm sm:text-base max-w-3xl mx-auto">
              Our platform is an AI-powered market research and data analysis tool that processes real market data and surfaces high-quality analysis insights.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {steps.map((step, idx) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="relative rounded-xl border border-gray-800 bg-[hsl(226,26%,9%)] p-5 sm:p-6 hover:border-[hsl(193,100%,50%)]/40 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[hsl(193,100%,50%,0.15)] border border-[hsl(193,100%,50%,0.35)] text-[hsl(193,100%,70%)] font-bold flex items-center justify-center">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-white text-lg sm:text-xl font-semibold">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[hsl(210,9%,80%)] text-sm sm:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 sm:mt-14 rounded-2xl border border-gray-800 bg-gradient-to-b from-[hsl(226,26%,9%)] to-[hsl(226,26%,7%)] p-6 sm:p-8"
          >
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
              <div className="flex-1">
                <h2 className="text-white text-xl sm:text-2xl font-bold">
                  Why TradeSagePro Fits Research Needs
                </h2>
                <ul className="mt-4 space-y-3 text-[hsl(210,9%,80%)] text-sm sm:text-base list-disc pl-5">
                  <li>Avoids noisy, low-confidence outputs from the start.</li>
                  <li>Surfaces insights only when market context aligns.</li>
                  <li>Keeps transparency and control over token usage.</li>
                </ul>
                <p className="mt-4 text-[hsl(210,9%,78%)] text-sm sm:text-base">
                  Our mission is to provide responsible research metrics, trust, and simplicity — all in one platform.
                </p>
              </div>

              <div className="w-full max-w-md mx-auto lg:mx-0">
                <div className="relative rounded-xl border border-gray-800 bg-[hsl(226,26%,10%)] p-5">
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-[hsl(193,100%,50%,0.05)] to-transparent pointer-events-none" />
                  <h3 className="text-white font-semibold">Fair Token Flow</h3>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg border border-gray-800 bg-[hsl(226,26%,12%)] p-3">
                      <div className="text-[hsl(210,9%,75%)]">Start Analysis</div>
                        <div className="text-white mt-1 font-medium">-1 Token</div>
                    </div>
                    <div className="rounded-lg border border-gray-800 bg-[hsl(226,26%,12%)] p-3">
                      <div className="text-[hsl(210,9%,75%)]">No Validated Output</div>
                        <div className="text-white mt-1 font-medium">+1 Token Return (per policy)</div>
                    </div>
                    <div className="rounded-lg border border-gray-800 bg-[hsl(226,26%,12%)] p-3 col-span-2">
                      <div className="text-[hsl(210,9%,75%)]">Validated Output</div>
                      <div className="text-white mt-1 font-medium">Structured Analysis Insight Delivered</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 relative z-10">
              <Button
                asChild
                className="w-full sm:w-auto bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-[1.02] transition"
              >
                <Link href="/pricing">View Pricing</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full sm:w-auto border-gray-700 text-white hover:bg-white/5"
              >
                <Link href="/start-trading">Start Analysis</Link>
              </Button>
            </div>
          </motion.div>
          
        </div>
      </section>
    </Layout>
  );
}
